import {Platform} from 'react-native';
import {onlyDigitsPipe} from '../pipes';
import {newGuid} from '../pipes/guid';
import {getHydratedItem, hydrateStorageKeys, removeHydratedItem, setHydratedItem} from '../utils/hydrateStorage';
import {getEsiaOAuthParams} from '../utils/esiaOAuthParams';
import {StorageKeys} from './index';

const snilsDigits = (value: string | null | undefined) => onlyDigitsPipe(value ?? '');

let attemptVersion = 0;
const webContextKey = 'cardReissueEsiaContext';

type WebContext = {
  id: string;
  pending: boolean;
  expectedSnils: string | null;
  oauthState: string | null;
};

const readWebContext = (): WebContext | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(webContextKey);
    const context = raw ? JSON.parse(raw) : null;
    if (
      !context ||
      typeof context.id !== 'string' ||
      typeof context.pending !== 'boolean' ||
      (context.expectedSnils !== null && typeof context.expectedSnils !== 'string') ||
      (context.oauthState !== null && typeof context.oauthState !== 'string')
    )
      return null;
    return context;
  } catch {
    return null;
  }
};

const writeWebContext = (context: WebContext) => {
  window.sessionStorage.setItem(webContextKey, JSON.stringify(context));
};

const expectedSnils = () =>
  Platform.OS === 'web'
    ? readWebContext()?.expectedSnils ?? null
    : getHydratedItem<string>(StorageKeys.cardReissueExpectedSnils);

export type CardReissueEsiaAttempt = {
  version: number;
  id: string | null;
  fromReissue: boolean;
  expectedSnils: string | null;
  oauthState: string | null;
};

export const hydrateCardReissueEsiaContext = async () => {
  if (Platform.OS !== 'web') {
    await hydrateStorageKeys([StorageKeys.cardReissueEsiaPending, StorageKeys.cardReissueExpectedSnils]);
  }
};

const saveExpectedSnils = async (snils: string | null | undefined) => {
  const digits = snilsDigits(snils) || null;
  if (digits) {
    await setHydratedItem(StorageKeys.cardReissueExpectedSnils, digits);
  } else {
    await removeHydratedItem(StorageKeys.cardReissueExpectedSnils);
  }
};

export const loadCardReissueExpectedSnils = async (): Promise<string | null> => {
  const digits = snilsDigits(expectedSnils());
  return digits || null;
};

export const clearCardReissueExpectedSnils = async () => {
  if (Platform.OS === 'web') {
    const context = readWebContext();
    if (context) writeWebContext({...context, expectedSnils: null});
    return;
  }
  await removeHydratedItem(StorageKeys.cardReissueExpectedSnils);
};

export const isCardReissueEsiaPending = () =>
  Platform.OS === 'web'
    ? Boolean(readWebContext()?.pending)
    : Boolean(getHydratedItem<boolean>(StorageKeys.cardReissueEsiaPending));

export const hasCardReissueEsiaContext = () => isCardReissueEsiaPending() || Boolean(expectedSnils());

export const captureCardReissueEsiaAttempt = (holderSnils?: string | null): CardReissueEsiaAttempt => ({
  version: attemptVersion,
  id: Platform.OS === 'web' ? readWebContext()?.id ?? null : null,
  fromReissue: isCardReissueEsiaPending(),
  expectedSnils: snilsDigits(expectedSnils() || holderSnils) || null,
  oauthState: Platform.OS === 'web' ? readWebContext()?.oauthState ?? null : null,
});

export const isCardReissueEsiaAttemptCurrent = (attempt: CardReissueEsiaAttempt) =>
  attempt.version === attemptVersion && (Platform.OS !== 'web' || attempt.id === (readWebContext()?.id ?? null));

export const bindCardReissueEsiaOAuthState = (attempt: CardReissueEsiaAttempt, link: string): boolean => {
  if (!isCardReissueEsiaAttemptCurrent(attempt)) return false;
  const context = readWebContext();
  const state = new URL(link).searchParams.get('state');
  if (!context?.pending || !state) return false;
  writeWebContext({...context, oauthState: state});
  return true;
};

export const isCardReissueEsiaOAuthReturn = (query?: string) => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  const params = getEsiaOAuthParams(query ?? window.location.search);
  const context = readWebContext();
  return Boolean(params && context?.pending && context.oauthState && context.oauthState === params.state);
};

export const startCardReissueEsiaPending = async (holderSnils?: string | null) => {
  attemptVersion += 1;
  if (Platform.OS === 'web') {
    writeWebContext({id: newGuid(), pending: true, expectedSnils: snilsDigits(holderSnils) || null, oauthState: null});
    return;
  }
  await Promise.all([setHydratedItem(StorageKeys.cardReissueEsiaPending, true), saveExpectedSnils(holderSnils)]);
};

export const stopCardReissueEsiaPending = async (): Promise<boolean> => {
  if (!isCardReissueEsiaPending()) return false;
  if (Platform.OS === 'web') {
    const context = readWebContext();
    if (context) writeWebContext({...context, pending: false});
    return true;
  }
  await removeHydratedItem(StorageKeys.cardReissueEsiaPending);
  return true;
};

export const clearCardReissueEsiaPending = async () => {
  attemptVersion += 1;
  if (Platform.OS === 'web') {
    try {
      if (typeof window !== 'undefined') window.sessionStorage.removeItem(webContextKey);
    } catch {
      // A browser that blocks sessionStorage cannot have a usable reissue context.
    }
    return;
  }
  await Promise.all([removeHydratedItem(StorageKeys.cardReissueEsiaPending), clearCardReissueExpectedSnils()]);
};
